import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Wrench, MapPin, Repeat } from 'lucide-react';
import { useTranslations } from '../../i18n';
import { theme } from '../../lib/theme';
import { formatCurrency } from '../../lib/conversions';
import { DynamicIcon } from '../../lib/icons';
import { normalizeExpenseTypeKey, getExpenseTypeNameFromData, SupportedLanguage } from '../../lib/expenseTypeHelpers';
import defaultExpenseTypesData from '../../data/defaultExpenseTypes.json';

export interface ExpenseItemProps {
    expense: {
        id: string;
        type: string;
        title?: string;
        amount?: number;
        date: string;
        description?: string;
        fuel_liters?: number;
        electricity_kwh?: number;
        fuel_type?: string;
        imageCount?: number;
        partCount?: number;

        location_id?: string;
        repeat_id?: string | null;
    };
    slug: string;
    isAdminView?: boolean;
    effectivePreferences: any;
    openInNewTab?: boolean;
    onClick?: () => void;
    className?: string;
    highlighted?: boolean;
}

export function ExpenseItem({
    expense,
    slug,
    isAdminView = false,
    effectivePreferences,
    openInNewTab = false,
    onClick,
    className = "",
    highlighted = false
}: ExpenseItemProps) {
    const navigate = useNavigate();
    const { t, language } = useTranslations();
    const currentLanguage = language as SupportedLanguage;

    const getExpenseTypeInfo = (typeKey: string) => {
        const key = normalizeExpenseTypeKey(typeKey);
        return (defaultExpenseTypesData as any)[key];
    };

    const getTypeName = (type: string) => {
        const info = getExpenseTypeInfo(type);
        if (info) return getExpenseTypeNameFromData(info, currentLanguage);
        return (t.expenses.typeLabels as any)[type] || type;
    };

    const typeInfo = getExpenseTypeInfo(expense.type);
    const displayTypeInfo = typeInfo || { icon: 'Circle', color: '#6B7280' };

    const handleClick = (e: React.MouseEvent) => {
        if (onClick) {
            onClick();
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        const prefix = isAdminView ? '/admin' : '';
        const url = `${prefix}/${slug}/expenses/${expense.id}`;

        if (openInNewTab) {
            window.open(url, '_blank');
        } else {
            navigate(url);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className={`w-full group text-left flex items-center gap-4 ${theme.spacing.cardPadding} transition-all duration-700 hover:bg-gray-50/80 dark:hover:bg-white/5 active:scale-[0.99] ${highlighted ? 'bg-yellow-50/80 dark:bg-yellow-900/20 z-10' : 'bg-transparent'} ${className}`}
        >
            <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${displayTypeInfo?.color || '#F3F4F6'}15` }}
            >
                <DynamicIcon name={displayTypeInfo?.icon || 'Circle'} size={20} color={displayTypeInfo?.color || '#6B7280'} />
            </div>

            <div className="flex-1 min-w-0 flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <h4 className={theme.typography.itemTitle}>
                            {expense.title || getTypeName(expense.type)}
                        </h4>
                        {((expense.type === 'fuel' && expense.fuel_liters) || (expense.type === 'electricity' && expense.electricity_kwh)) && (
                            <span className={`px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-white/10 text-[11px] font-bold text-gray-500 dark:text-gray-400 tracking-wide`}>
                                {expense.type === 'fuel' && expense.fuel_liters ? (
                                    `${expense.fuel_liters.toFixed(2)} ${effectivePreferences?.volume_unit === 'gallons' ? t.common.units.gallons : t.common.units.liters}`
                                ) : expense.type === 'electricity' && expense.electricity_kwh ? (
                                    `${expense.electricity_kwh.toFixed(2)} kWh`
                                ) : null}
                            </span>
                        )}
                    </div>
                    {expense.description && (
                        <p className="text-[13px] text-gray-500 dark:text-gray-400 font-medium leading-relaxed line-clamp-3 italic mt-1 whitespace-pre-wrap">
                            {(expense.description || '').replace(/\\\n/g, '\n')}
                        </p>
                    )}
                </div>

                <div className="text-right flex-shrink-0 flex flex-col items-end gap-0.5">
                    {expense.type !== 'info' && (
                        <span className={theme.typography.itemTitle}>
                            {effectivePreferences ? formatCurrency(expense.amount || 0, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${(expense.amount || 0).toFixed(2)}`}
                        </span>
                    )}
                    <span className={`${theme.typography.itemSubtitle} whitespace-nowrap`}>
                        {expense.date && !isNaN(new Date(expense.date).getTime()) ? new Date(expense.date).toLocaleDateString(currentLanguage, { day: '2-digit', month: 'short' }) : ''}
                    </span>
                    {((expense.imageCount || 0) > 0 || (expense.partCount || 0) > 0 || !!expense.location_id || !!expense.repeat_id) && (
                        <div className="flex items-center gap-2 mt-0.5">
                            {(expense.imageCount || 0) > 0 && (
                                <span className="flex items-center gap-1 text-blue-500 dark:text-blue-400">
                                    <ImageIcon size={14} />
                                    <span className="text-xs font-medium">{expense.imageCount}</span>
                                </span>
                            )}
                            {(expense.partCount || 0) > 0 && (
                                <span className="flex items-center gap-1 text-orange-500">
                                    <Wrench size={14} />
                                    <span className="text-xs font-medium">{expense.partCount}</span>
                                </span>
                            )}
                            {expense.location_id && (
                                <span className="flex items-center gap-1 text-emerald-500 dark:text-emerald-400">
                                    <MapPin size={14} />
                                </span>
                            )}
                            {expense.repeat_id && (
                                <span className="flex items-center gap-1 text-blue-500" title={t.repeatingExpenses?.isRepeating || 'Repeating'}>
                                    <Repeat size={14} />
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </button>
    );
}
